<template>
    <div class="map-container">
        <div id="amenities-map" class="map"></div>
    </div>
</template>

<script>
import { Maps } from "tortilla";
import { mapGetters } from 'vuex';
import mapStylesDefault from "../vendor/google-map-snazzymaps";
import withBreakpoint from './breakpoints/with-breakpoint';
import { createMap, createMarker, fitMapBounds, createInfoWindow } from '../utils/mapUtils';

const communityPopoverContent = (community) => `
    <div class="community-flyout__logo">
        <img alt="${community.name} logo" src="${community.logo}">
    </div>
`;

export default {
    mixins: [withBreakpoint],
    props: [
        'markerData',
        'community',
        'availableAmenities'
    ],
    data: () => ({
        gm: {},
        mapInstance: {},
        amenityMarkers: [],
        communityMarkers: [],
        selectedAmenity: 0,
        infoWindow: undefined,
        icons: {
            amenity: '/images/site/maps/white-pin.svg',
            community: '/images/site/maps/white-pin.svg',
            schools: '/images/site/maps/pin-schools.svg',
            dining: '/images/site/maps/pin-dining.svg',
            shopping: '/images/site/maps/pin-shopping.svg',
            grocery: '/images/site/maps/pin-grocery.svg',
            recreation: '/images/site/maps/pin-recreation.svg',
            entertainment: '/images/site/maps/pin-entertainment.svg',
            pharmacy: '/images/site/maps/pin-pharmacy.svg',
            hospitals: '/images/site/maps/pin-hospitals.svg',
            airports: '/images/site/maps/pin-airports.svg'
        },
        isFilterPanelActive: false
    }),
    computed: {
        ...mapGetters({
            appData: 'app/getData',
        }),
        mapStyles() {
            return mapStylesDefault;
        },
        markers() {
            return this.communityMarkers.concat(this.amenityMarkers);
        },
        communityPosition() {
            return {
                lat: this.community.latitude,
                lng: this.community.longitude
            };
        }
    },
    watch: {
        selectedAmenity(val) {
            this.populateAmenities(this.appData.markers, val);
        },
        mapInstance(val) {
            this.populateAmenities(this.appData.markers, 0);
        }
    },
    methods: {
        async initMap() {
            try {
                this.mapInstance = await createMap('amenities-map');
                this.infoWindow = createInfoWindow();
            } catch (error) {
                console.error('Failed to initialize map:', error);
            }
        },
        async populateAmenities(markers, selectedType) {
            if (this.amenityMarkers.length) {
                this.amenityMarkers.forEach(marker => marker.setMap(null));
                this.amenityMarkers = [];
            }

            const filteredMarkers = selectedType ? 
                markers.filter(m => m.type === selectedType) : 
                markers;

            const placeMarkers = await Promise.all(
                filteredMarkers.map(place => createMarker({
                    map: this.map,
                    position: { 
                        lat: place.latitude, 
                        lng: place.longitude 
                    },
                    icon: place.icon,
                    title: place.title,
                    className: 'amenity-marker'
                }))
            );

            this.amenityMarkers = placeMarkers;
            fitMapBounds(this.map, this.markers);
        },
        async initCommunityMarker() {
            const marker = await createMarker({
                map: this.map,
                position: this.communityPosition,
                icon: this.icons.community,
                size: { width: 36, height: 36 },
                title: this.community.name,
                className: 'community-marker'
            });

            marker.addListener('click', () => {
                if (this.infoWindow) {
                    this.infoWindow.close();
                    this.infoWindow.setContent(communityPopoverContent(this.community));
                    this.infoWindow.open({
                        anchor: marker,
                        map: this.map
                    });
                }
                this.map.panTo(marker.position);
                if (this.currentBreakpoint === 'phone' || this.currentBreakpoint === 'tablet') {
                    this.map.panBy(0, -200);
                }
            });

            this.communityMarkers.push(marker);
        },
        populateAmenities(amenities, filter) {
            if (!amenities) {
                return;
            }

            const infoWindow = this.createInfoWindow();
            const placeMarkers = [];

            if (typeof filter !== 'undefined') {
                amenities = amenities.filter((amenity) => amenity.category_id === filter);
            }

            if (!amenities.length) {
                return;
            }

            amenities.forEach((place, index) => {
                const address = `${place.address}, ${place.city}, ${place.state} ${place.zip}`;
                const template = this.getTemplate('custom', place);
                const marker = new this.gm.Marker({
                    map: this.mapInstance,
                    icon: {
                        url: this.icons[this.availableAmenities[filter].name.toLowerCase()],
                        scaledSize: new this.gm.Size(36, 36)
                    },
                    title: place.name,
                    position: {
                        lat: place.latitude,
                        lng: place.longitude
                    }
                });
                marker.addListener('click', () => {
                    if (this.infoWindow) {
                        this.infoWindow.close();
                        this.infoWindow.setContent(template);
                        this.infoWindow.open(marker.getMap(), marker);
                    }
                    this.mapInstance.panTo(marker.position);
                    if (this.currentBreakpoint === 'phone' || this.currentBreakpoint === 'tablet') {
                        this.mapInstance.panBy(0, -200);
                    }
                });

                placeMarkers.push(marker);
            });

            this.amenityMarkers = placeMarkers;
        },
        initMap(map) {
            this.$gmaps().then(gm => {
                this.gm = gm;
                this.mapInstance = map;
                this.initCommunityMarker();

                if(this.appData.markers.length) {
                    this.selectAmenity(this.appData.markers[0].category_id);
                }
            });
        },
        createInfoWindow() {
            if (!this.infoWindow) {
                this.infoWindow = new this.gm.InfoWindow({});
            }
            return this.infoWindow;
        },
        toggleFilterPanel(closePanel) {
            this.isFilterPanelActive = closePanel ? false : !this.isFilterPanelActive;
        },
        isSelectedAmenity(amenity) {
            return this.selectedAmenity === amenity;
        },
        selectAmenity(val) {
            this.selectedAmenity = val;
            this.toggleFilterPanel(true);
        },
        getTemplate(type, place) {
            let template = '';
            if (type === 'custom') {
                const vicinity = `${place.address}, ${place.city}, ${place.state} ${place.zip}`;
                if (place.phone) {
                    template = `
                        <div class="community-microsite__flyout">
                            <div class="community-microsite__flyout-image community-microsite__flyout-image--contain" style="background-image:url(${place.images.logo.medium});"></div>
                            <div class="community-microsite__flyout-data">
                                <div class="community-microsite__flyout-title">${place.name}</div>
                                <div class="community-microsite__flyout-address">${vicinity}</div>
                                <div class="community-microsite__flyout-phone">${place.phone}</div>
                            </div>
                        </div>
                    `;
                } else if (place.website) {
                     template = `
                        <div class="community-microsite__flyout">
                            <div class="community-microsite__flyout-image community-microsite__flyout-image--contain" style="background-image:url(${place.images.logo.medium});"></div>
                            <div class="community-microsite__flyout-data">
                                <div class="community-microsite__flyout-title">${place.name}</div>
                                <div class="community-microsite__flyout-address">${vicinity}</div>
                                <div class="community-microsite__flyout-phone"><a href="${place.website}" target="_blank">Website</a></div>
                            </div>
                        </div>
                    `;
                } else {
                     template = `
                        <div class="community-microsite__flyout">
                            <div class="community-microsite__flyout-image community-microsite__flyout-image--contain" style="background-image:url(${place.images.logo.medium});"></div>
                            <div class="community-microsite__flyout-data">
                                <div class="community-microsite__flyout-title">${place.name}</div>
                                <div class="community-microsite__flyout-address">${vicinity}</div>
                            </div>
                        </div>
                    `;
                }
            }
            return template
        }
    }
};
</script>

<style scoped>
.map-container {
    width: 100%;
    height: 100%;
    min-height: 400px;
}
.map {
    width: 100%;
    height: 100%;
}
</style>



// WEBPACK FOOTER //
// resources/components/apps/community-amenities.vue